import React, { useEffect, useState, useCallback } from 'react';
import { Car, Search, RefreshCw, MapPin, Camera as CameraIcon, Plus, Trash2, ArrowUpRight, ArrowDownLeft } from 'lucide-react';

const GlassPanel: React.FC<{ children: React.ReactNode; className?: string; title?: string; icon?: any }> = ({ children, className = '', title, icon: Icon }) => (
  <div className={`bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl ${className}`}>
    {(title || Icon) && (
      <div className="px-6 py-4 border-b border-white/5 flex items-center gap-3 bg-white/[0.02]">
        {Icon && <Icon className="w-5 h-5 text-blue-400" />}
        {title && <h3 className="text-sm font-bold tracking-tight text-blue-100/90 uppercase">{title}</h3>}
      </div>
    )}
    <div className="p-6">{children}</div>
  </div>
);

function authHeaders() {
  const token = localStorage.getItem('csense_auth_token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
}

interface PlateEvent {
  id: string;
  plate: string;
  camera: string;
  location: string;
  confidence: number;
  imageUrl?: string;
  timestamp: Date;
}

interface RegisteredVehicle {
  plate_number: string;
  owner_name: string;
  description?: string;
}

interface VehicleLogEntry {
  id: string;
  plate_number: string;
  owner_name: string;
  event_type: 'taken' | 'parked';
  duration_away_seconds?: number;
  camera: string;
  imageUrl?: string;
  timestamp: Date;
}

export function VehicleDetection() {
  const [events, setEvents] = useState<PlateEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [vehicles, setVehicles] = useState<RegisteredVehicle[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(true);
  const [plateInput, setPlateInput] = useState('');
  const [ownerInput, setOwnerInput] = useState('');
  const [descInput, setDescInput] = useState('');
  const [registering, setRegistering] = useState(false);

  const [vehicleLog, setVehicleLog] = useState<VehicleLogEntry[]>([]);
  const [vehicleLogLoading, setVehicleLogLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('csense_auth_token');
      const response = await fetch('/api/alerts?usecase=anpr&limit=100', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const mapped: PlateEvent[] = (data.alerts || []).map((a: any) => ({
          id: a.id,
          plate: a.metadata?.plate_number || (a.message || '').replace('License plate detected: ', '') || 'UNKNOWN',
          camera: a.cameraName || 'Unknown Camera',
          location: a.location || 'Unknown',
          confidence: Math.round((parseFloat(a.confidence) || 0) * (a.confidence <= 1 ? 100 : 1)),
          imageUrl: a.image_url || undefined,
          timestamp: a.timestamp ? new Date(a.timestamp) : new Date()
        }));
        setEvents(mapped);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchVehicles = useCallback(async () => {
    setVehiclesLoading(true);
    try {
      const response = await fetch('/api/vehicles', { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setVehicles(data.vehicles || []);
      }
    } finally {
      setVehiclesLoading(false);
    }
  }, []);

  const fetchVehicleLog = useCallback(async () => {
    setVehicleLogLoading(true);
    try {
      const response = await fetch('/api/vehicle-log', { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        const mapped: VehicleLogEntry[] = (data.log || [])
          .map((a: any, idx: number) => ({
            id: a.alert_id || `${a.metadata?.plate_number}-${idx}`,
            plate_number: a.metadata?.plate_number || 'UNKNOWN',
            owner_name: a.metadata?.owner_name || '',
            event_type: a.metadata?.event_type || (a.type === 'vehicle_taken' ? 'taken' : 'parked'),
            duration_away_seconds: a.metadata?.duration_away_seconds,
            camera: a.camera_name || 'Unknown Camera',
            imageUrl: a.image_url || undefined,
            timestamp: a.timestamp ? new Date(a.timestamp) : new Date()
          }))
          .sort((a: VehicleLogEntry, b: VehicleLogEntry) => b.timestamp.getTime() - a.timestamp.getTime());
        setVehicleLog(mapped);
      }
    } finally {
      setVehicleLogLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchVehicles();
    fetchVehicleLog();
  }, [fetchEvents, fetchVehicles, fetchVehicleLog]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plateInput.trim() || !ownerInput.trim()) return;
    setRegistering(true);
    try {
      const response = await fetch('/api/vehicles', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          plate_number: plateInput.trim().toUpperCase(),
          owner_name: ownerInput.trim(),
          description: descInput.trim() || undefined
        })
      });
      if (response.ok) {
        setPlateInput('');
        setOwnerInput('');
        setDescInput('');
        fetchVehicles();
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleDelete = async (plate: string) => {
    try {
      const response = await fetch(`/api/vehicles/${encodeURIComponent(plate)}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (response.ok) fetchVehicles();
    } catch {}
  };

  const filtered = events.filter(e =>
    e.plate.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.camera.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const uniquePlates = new Set(events.map(e => e.plate)).size;

  return (
    <div className="min-h-screen bg-[#0a0b14] text-white p-6 space-y-8 pb-32">
      <div>
        <h1 className="text-3xl font-black tracking-tighter uppercase flex items-center gap-3"><Car className="w-8 h-8 text-blue-400" /> Vehicle Detection</h1>
        <p className="text-sm text-blue-100/40 mt-1 font-medium tracking-wide">Automatic number plate recognition events from your ANPR-enabled cameras, plus Personal Vehicle Watch for registered plates.</p>
      </div>

      <GlassPanel className="p-0" title="Register Personal Vehicle" icon={Plus}>
        <form onSubmit={handleRegister} className="flex flex-wrap gap-4 items-end mb-6">
          <div className="flex-1 min-w-[160px]">
            <label className="text-[10px] uppercase tracking-widest text-blue-100/40 font-bold mb-1 block">Plate Number</label>
            <input value={plateInput} onChange={e => setPlateInput(e.target.value)} placeholder="e.g. ABC123" className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white placeholder:text-blue-100/20 outline-none focus:border-blue-500/40 font-mono uppercase" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-[10px] uppercase tracking-widest text-blue-100/40 font-bold mb-1 block">Owner Name</label>
            <input value={ownerInput} onChange={e => setOwnerInput(e.target.value)} placeholder="e.g. John Smith" className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white placeholder:text-blue-100/20 outline-none focus:border-blue-500/40" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-[10px] uppercase tracking-widest text-blue-100/40 font-bold mb-1 block">Description (optional)</label>
            <input value={descInput} onChange={e => setDescInput(e.target.value)} placeholder="e.g. White Toyota Hilux" className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white placeholder:text-blue-100/20 outline-none focus:border-blue-500/40" />
          </div>
          <button type="submit" disabled={registering} className="px-5 py-3 bg-blue-500/20 border border-blue-500/30 rounded-xl text-blue-200 hover:bg-blue-500/30 flex items-center gap-2 font-bold text-sm disabled:opacity-50">
            <Plus className="w-4 h-4" /> {registering ? 'Registering...' : 'Register'}
          </button>
        </form>

        {vehiclesLoading ? (
          <div className="py-10 text-center text-blue-100/20 text-sm uppercase tracking-widest">Loading...</div>
        ) : vehicles.length === 0 ? (
          <div className="py-10 text-center text-blue-100/20 text-sm uppercase tracking-widest">No vehicles registered yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {vehicles.map(v => (
              <div key={v.plate_number} className="flex items-center justify-between gap-3 bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="min-w-0">
                  <p className="text-sm font-mono font-black text-white tracking-widest">{v.plate_number}</p>
                  <p className="text-[11px] text-blue-100/40 font-medium truncate">{v.owner_name}{v.description ? ` · ${v.description}` : ''}</p>
                </div>
                <button onClick={() => handleDelete(v.plate_number)} className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 flex-shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </GlassPanel>

      <GlassPanel className="p-0" title="Personal Vehicle Watch Log (Taken / Parked)" icon={Car}>
        <div className="flex justify-end mb-4">
          <button onClick={fetchVehicleLog} className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-blue-100/60 hover:text-white flex items-center gap-2 text-sm">
            <RefreshCw className={`w-4 h-4 ${vehicleLogLoading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
        {vehicleLogLoading ? (
          <div className="py-10 text-center text-blue-100/20 text-sm uppercase tracking-widest">Loading...</div>
        ) : vehicleLog.length === 0 ? (
          <div className="py-10 text-center text-blue-100/20 text-sm uppercase tracking-widest">No taken/parked events yet.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {vehicleLog.map(e => (
              <div key={e.id} className="flex gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
                <div className="w-28 h-20 rounded-xl bg-black/40 overflow-hidden flex-shrink-0 flex items-center justify-center">
                  {e.imageUrl ? <img src={e.imageUrl} className="w-full h-full object-cover" /> : <Car className="w-6 h-6 text-blue-100/10" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-mono font-black text-white tracking-widest">{e.plate_number}</p>
                    {e.event_type === 'taken' ? (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-amber-400 uppercase"><ArrowUpRight className="w-3 h-3" /> Taken</span>
                    ) : (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-400 uppercase"><ArrowDownLeft className="w-3 h-3" /> Parked</span>
                    )}
                  </div>
                  <p className="text-[11px] text-blue-100/40 font-medium">{e.owner_name}</p>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-blue-100/40 uppercase font-bold">
                    <span className="flex items-center gap-1"><CameraIcon className="w-3 h-3" />{e.camera}</span>
                    {e.event_type === 'parked' && typeof e.duration_away_seconds === 'number' && (
                      <span>Away {(e.duration_away_seconds / 60).toFixed(1)} min</span>
                    )}
                  </div>
                  <span className="text-[10px] text-blue-100/20 font-mono block mt-2">{e.timestamp.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassPanel>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <GlassPanel><p className="text-[10px] uppercase tracking-widest text-blue-100/40 font-bold mb-1">Total Reads</p><h4 className="text-2xl font-black text-white">{events.length}</h4></GlassPanel>
        <GlassPanel><p className="text-[10px] uppercase tracking-widest text-blue-100/40 font-bold mb-1">Unique Plates</p><h4 className="text-2xl font-black text-white">{uniquePlates}</h4></GlassPanel>
        <GlassPanel><p className="text-[10px] uppercase tracking-widest text-blue-100/40 font-bold mb-1">Cameras Reporting</p><h4 className="text-2xl font-black text-white">{new Set(events.map(e => e.camera)).size}</h4></GlassPanel>
      </div>

      <GlassPanel className="p-0" title="Plate Recognition Log" icon={Car}>
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-100/20" />
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search plate or camera..." className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-12 pr-4 text-white placeholder:text-blue-100/20 outline-none focus:border-blue-500/40" />
          </div>
          <button onClick={fetchEvents} className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-blue-100/60 hover:text-white flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="py-20 text-center text-blue-100/20 text-sm uppercase tracking-widest">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-blue-100/20 text-sm uppercase tracking-widest">No plate recognition events yet.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map(e => (
              <div key={e.id} className="flex gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
                <div className="w-28 h-20 rounded-xl bg-black/40 overflow-hidden flex-shrink-0 flex items-center justify-center">
                  {e.imageUrl ? <img src={e.imageUrl} className="w-full h-full object-cover" /> : <Car className="w-6 h-6 text-blue-100/10" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-mono font-black text-white tracking-widest">{e.plate}</p>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-blue-100/40 uppercase font-bold">
                    <span className="flex items-center gap-1"><CameraIcon className="w-3 h-3" />{e.camera}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-blue-100/20 font-mono">{e.timestamp.toLocaleString()}</span>
                    <span className="text-[10px] text-emerald-400 font-bold">{e.confidence}% confidence</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassPanel>
    </div>
  );
}
